import { CompleteBooking } from "../../types/Booking";
import {
  BookingScope,
  BookingStatus,
  BookingType,
} from "../../enums/BookingScope";
import { getBookingTypeLabel, getStatusLabel } from "../../utils/stringUtil";
import { MatchType } from "../../types/Result";
import { Input, Select, Tooltip } from "antd";
import { useCallback, useEffect, useState } from "react";
import { Structures } from "../../api/Structure";
import { useToasts } from "../../contexts/toasts";
import { Option } from "../../models/generics";
import { TailSpin } from "react-loader-spinner";
import "./Booking.css";
import TextArea from "antd/es/input/TextArea";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEnvelope,
  faGripVertical,
  faLink,
  faPhone,
  faRightLeft,
} from "@fortawesome/free-solid-svg-icons";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import classNames from "classnames";
import {
  buildBookingShareText,
  copyTextToClipboard,
  getWhatsAppShareUrl,
} from "../../utils/shareBooking";
import FootballMatchParticipants from "./FootballMatchParticipants";
import BasketMatchParticipants from "./BasketMatchParticipants";
import BookingColorPicker from "./BookingColorPicker";
import BookingTypeSelect from "./BookingTypeSelect";
import { isValidHexColor } from "../../utils/bookingColor";

type Props = {
  booking: CompleteBooking;
  mode: "view" | "edit";
  bookingData: {
    matchType?: MatchType;
    bookingType?: BookingType;
    scope: BookingScope;
    inviteLevel?: number;
    coach?: string;
    player1Team1?: string;
    player2Team1?: string;
    player1Team2?: string;
    player2Team2?: string;
    notes?: string;
    color?: string;
    customType?: string;
  };
  handleFieldChange: (key: string) => (value: string) => void;
  handleFieldClear: (key: string) => () => void;
  handleInviteLevelChange: (e: number) => void;
};

// Slot dei giocatori di una partita (padel/tennis): chiavi di bookingData.
type PlayerSlotKey =
  | "player1Team1"
  | "player2Team1"
  | "player1Team2"
  | "player2Team2";
type PairKey = "team1" | "team2";

const PAIR_SLOTS: Record<PairKey, PlayerSlotKey[]> = {
  team1: ["player1Team1", "player2Team1"],
  team2: ["player1Team2", "player2Team2"],
};

// MIME custom così il drop accetta solo i nostri trascinamenti.
const SLOT_MIME = "application/x-getmove-player-slot";
const PAIR_MIME = "application/x-getmove-player-pair";

// Il dragleave scatta anche passando sui figli: lo ignoriamo se il puntatore
// resta dentro il contenitore.
const leavesTarget = (e: React.DragEvent) =>
  !e.currentTarget.contains(e.relatedTarget as Node | null);

const getPlayerName = (player?: { name?: string; familyName?: string } | null): string => {
  if (!player) return "N.D.";
  const fullName = `${player.name || ""} ${player.familyName || ""}`.trim();
  return fullName || "Utente sconosciuto";
};

const withUnknownOption = (options: Option[], value?: string): Option[] => {
  if (!value || options.some((o) => o.value === value)) return options;
  return [...options, { label: "Utente sconosciuto", value }];
};

const Spinner = () => (
  <div className="d-flex justify-content-center py-2">
    <TailSpin height={20} width={20} color="#f25d0b" radius="1" />
  </div>
);

export default function BookingModal({
  booking,
  mode,
  bookingData,
  handleFieldChange,
  handleFieldClear,
  handleInviteLevelChange,
}: Props) {
  const { showToast } = useToasts();
  const [usersList, setUsersList] = useState<Option[]>();
  const [coachesList, setCoachesList] = useState<Option[]>();

  const initFavorites = useCallback(() => {
    Structures.getHostFavorites().then(
      ({ data }) => {
        setUsersList(
          data.list.map((user) => ({
            label: `${user.user.name} ${user.user.familyName}`,
            value: user.userId,
          }))
        );
      },
      () => {
        showToast("players-error", "Impossibile ottenere la lista dei giocatori", "", "danger");
        setUsersList([]);
      }
    );
  }, [showToast]);

  const initCoaches = useCallback(() => {
    Structures.getStructureCoaches().then(
      ({ data }) => {
        setCoachesList(
          data.list.map((coach) => ({
            label: `${coach.user.name} ${coach.user.familyName}`,
            value: coach.userId,
          }))
        );
      },
      () => {
        showToast("coaches-error", "Impossibile ottenere la lista dei maestri", "", "danger");
        setCoachesList([]);
      }
    );
  }, [showToast]);

  useEffect(() => {
    // Calcio (sportId=3) e basket (sportId=4) usano i favoriti per aggiungere
    // i partecipanti, ma non hanno maestri.
    if (mode === "edit" && [1, 2, 3, 4, 7].includes(booking.sportId)) {
      initFavorites();
    }
    if (mode === "edit" && [1, 2, 7].includes(booking.sportId)) {
      initCoaches();
    }
  }, [initFavorites, initCoaches, mode, booking]);

  const handleNoteChange = useCallback(
    () => (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      handleFieldChange("notes")(e.target.value);
    },
    [handleFieldChange]
  );

  const handleColorChange = useCallback(
    (color?: string) => {
      if (color) handleFieldChange("color")(color);
      else handleFieldClear("color")();
    },
    [handleFieldChange, handleFieldClear]
  );

  // Tipologia personalizzata (es. "Manutenzione"): non è una partita aperta,
  // quindi la prenotazione resta privata.
  const handleBookingTypeChange = useCallback(
    (bookingType: BookingType, customType?: string) => {
      handleFieldChange("bookingType")(bookingType);
      if (customType === undefined) {
        handleFieldClear("customType")();
      } else {
        handleFieldChange("customType")(customType);
        if (bookingData.scope !== BookingScope.Private)
          handleFieldChange("scope")(BookingScope.Private);
      }
    },
    [handleFieldChange, handleFieldClear, bookingData.scope]
  );

  const isCustomTypeEdit = mode === "edit" && bookingData.customType !== undefined;

  const renderUserTooltip = useCallback(() => (
    <div className="text-center">
      <span>{booking?.user.name} {booking?.user.familyName}</span><br />
      <span><FontAwesomeIcon icon={faEnvelope} className="me-2" />{booking?.user.email || "Nessuna e-mail"}</span><br />
      <span><FontAwesomeIcon icon={faPhone} className="me-2" />{booking?.user.phoneNumber}</span>
    </div>
  ), [booking]);

  const isDouble = booking.sportId === 1 || booking.match?.matchType === MatchType.Double;

  // ── Drag & drop fra gli slot giocatore (solo in modifica) ──
  // Trascinando la maniglia di uno slot su un altro slot i due giocatori si
  // scambiano; trascinando l'etichetta "Coppia 1" su "Coppia 2" si invertono
  // le coppie intere. Lo scambio modifica solo il form: si salva col pulsante
  // Salva come ogni altra modifica.
  const [draggingSlot, setDraggingSlot] = useState<PlayerSlotKey | null>(null);
  const [dragOverSlot, setDragOverSlot] = useState<PlayerSlotKey | null>(null);
  const [draggingPair, setDraggingPair] = useState<PairKey | null>(null);
  const [dragOverPair, setDragOverPair] = useState<PairKey | null>(null);

  const assignSlot = useCallback(
    (key: PlayerSlotKey, value?: string) => {
      if (value) handleFieldChange(key)(value);
      else handleFieldClear(key)();
    },
    [handleFieldChange, handleFieldClear]
  );

  const swapSlots = useCallback(
    (a: PlayerSlotKey, b: PlayerSlotKey) => {
      if (a === b) return;
      const valueA = bookingData[a];
      const valueB = bookingData[b];
      if (!valueA && !valueB) return;
      assignSlot(a, valueB);
      assignSlot(b, valueA);
    },
    [assignSlot, bookingData]
  );

  const swapPairs = useCallback(() => {
    swapSlots("player1Team1", "player1Team2");
    if (isDouble) swapSlots("player2Team1", "player2Team2");
  }, [swapSlots, isDouble]);

  const resetDrag = useCallback(() => {
    setDraggingSlot(null);
    setDragOverSlot(null);
    setDraggingPair(null);
    setDragOverPair(null);
  }, []);

  const slotHandleProps = useCallback(
    (key: PlayerSlotKey) => ({
      draggable: true,
      onDragStart: (e: React.DragEvent) => {
        e.stopPropagation();
        e.dataTransfer.setData(SLOT_MIME, key);
        e.dataTransfer.effectAllowed = "move";
        setDraggingSlot(key);
      },
      onDragEnd: resetDrag,
    }),
    [resetDrag]
  );

  // Nota: la pagina prenotazioni è dentro il DndProvider di react-dnd (usato
  // dal calendario). Il suo backend ascolta dragover/drop sulla finestra e per
  // i trascinamenti che non riconosce imposta dropEffect = "none", annullando
  // il drop: per questo fermiamo la propagazione dei nostri eventi.
  const slotDropProps = useCallback(
    (key: PlayerSlotKey) => ({
      onDragEnter: (e: React.DragEvent) => {
        if (!e.dataTransfer.types.includes(SLOT_MIME)) return;
        e.preventDefault();
        e.stopPropagation();
      },
      onDragOver: (e: React.DragEvent) => {
        if (!e.dataTransfer.types.includes(SLOT_MIME)) return;
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = "move";
        if (dragOverSlot !== key) setDragOverSlot(key);
      },
      onDragLeave: (e: React.DragEvent) => {
        if (leavesTarget(e) && dragOverSlot === key) setDragOverSlot(null);
      },
      onDrop: (e: React.DragEvent) => {
        if (!e.dataTransfer.types.includes(SLOT_MIME)) return;
        e.preventDefault();
        e.stopPropagation();
        const from = e.dataTransfer.getData(SLOT_MIME) as PlayerSlotKey;
        resetDrag();
        if (from && from !== key) swapSlots(from, key);
      },
    }),
    [dragOverSlot, resetDrag, swapSlots]
  );

  const pairHandleProps = useCallback(
    (pair: PairKey) => ({
      draggable: true,
      onDragStart: (e: React.DragEvent) => {
        e.stopPropagation();
        e.dataTransfer.setData(PAIR_MIME, pair);
        e.dataTransfer.effectAllowed = "move";
        setDraggingPair(pair);
      },
      onDragEnd: resetDrag,
    }),
    [resetDrag]
  );

  const pairDropProps = useCallback(
    (pair: PairKey) => ({
      onDragEnter: (e: React.DragEvent) => {
        if (!e.dataTransfer.types.includes(PAIR_MIME)) return;
        e.preventDefault();
        e.stopPropagation();
      },
      onDragOver: (e: React.DragEvent) => {
        if (!e.dataTransfer.types.includes(PAIR_MIME)) return;
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = "move";
        if (dragOverPair !== pair) setDragOverPair(pair);
      },
      onDragLeave: (e: React.DragEvent) => {
        if (leavesTarget(e) && dragOverPair === pair) setDragOverPair(null);
      },
      onDrop: (e: React.DragEvent) => {
        if (!e.dataTransfer.types.includes(PAIR_MIME)) return;
        e.preventDefault();
        e.stopPropagation();
        const from = e.dataTransfer.getData(PAIR_MIME) as PairKey;
        resetDrag();
        if (from && from !== pair) swapPairs();
      },
    }),
    [dragOverPair, resetDrag, swapPairs]
  );

  const slotClassName = (key: PlayerSlotKey) =>
    classNames("bm-slot", {
      "bm-slot--dragging": draggingSlot === key,
      "bm-slot--dragover": dragOverSlot === key && draggingSlot !== key,
    });

  const pairClassName = (pair: PairKey) =>
    classNames("bm-field bm-pair", {
      "bm-pair--dragging": draggingPair === pair,
      "bm-pair--dragover": dragOverPair === pair && draggingPair !== pair,
    });

  const renderSlotHandle = (key: PlayerSlotKey) => (
    <span
      className="bm-slot-handle"
      title="Trascina su un altro giocatore per scambiarli"
      {...slotHandleProps(key)}
    >
      <FontAwesomeIcon icon={faGripVertical} />
    </span>
  );

  const ALL_SLOTS = PAIR_SLOTS.team1.concat(PAIR_SLOTS.team2);
  const hasAnyPlayer = ALL_SLOTS.some((key) => !!bookingData[key]);

  // Opzioni di uno slot: tutti i favoriti tranne i giocatori già scelti negli
  // altri slot (valori correnti del form, così restano coerenti dopo uno
  // scambio), più il valore attuale se non è fra i favoriti.
  const optionsFor = (key: PlayerSlotKey): Option[] => {
    const taken = ALL_SLOTS.filter((k) => k !== key)
      .map((k) => bookingData[k])
      .filter((v): v is string => !!v);
    return withUnknownOption(
      (usersList ?? []).filter((user) => !taken.includes(user.value)),
      bookingData[key]
    );
  };

  // Condivisione partita (come lo ShareButton dell'app): solo in vista e
  // solo per prenotazioni attive e non di tipologia personalizzata.
  const canShare =
    mode === "view" &&
    !booking.customType &&
    booking.status !== BookingStatus.Cancelled;
  const shareText = canShare ? buildBookingShareText(booking) : "";

  const handleCopyShareLink = useCallback(async () => {
    const ok = await copyTextToClipboard(shareText);
    showToast(
      ok ? "share-copied" : "share-copy-error",
      ok ? "Link copiato" : "Impossibile copiare il link",
      "",
      ok ? "success" : "danger"
    );
  }, [shareText, showToast]);

  const statusClass =
    booking.status === BookingStatus.Pending ? "bm-status-pending"
    : booking.status === BookingStatus.Completed ? "bm-status-completed"
    : "bm-status-cancelled";

  return (
    <div className="bm-container">

      {/* ── Sezione: dettagli prenotazione ── */}
      <div className="bm-section">

        {/* Tipo */}
        <div className="bm-info-row">
          <span className="bm-label">Tipo</span>
          {mode === "view" ? (
            <span className="bm-value">
              {getBookingTypeLabel(booking)}
              {!booking.customType && booking.bookingType === BookingType.Training && booking.coachId && (
                <span className="bm-coach-badge">
                  {booking.coach?.name} {booking.coach?.familyName}
                </span>
              )}
            </span>
          ) : (
            <div style={{ minWidth: 180 }}>
              <BookingTypeSelect
                bookingType={bookingData.bookingType}
                customType={bookingData.customType}
                onChange={handleBookingTypeChange}
                allowCustom={booking.createdByHost}
              />
            </div>
          )}
        </div>

        {/* Coach (edit, solo se allenamento) */}
        {mode === "edit" && !isCustomTypeEdit && bookingData.bookingType === BookingType.Training && [1, 2, 7].includes(booking.sportId) && (
          <div className="bm-field">
            <span className="bm-edit-label">Maestro</span>
            {coachesList ? (
              <Select
                showSearch
                filterOption={(input, option) =>
                  (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                }
                allowClear
                onClear={handleFieldClear("coach")}
                options={coachesList}
                className="w-100 p-0"
                placeholder="Scegli il maestro"
                value={bookingData.coach}
                onSelect={handleFieldChange("coach")}
                getPopupContainer={(trigger) => trigger.parentNode}
              />
            ) : <Spinner />}
          </div>
        )}

        {/* Data e ora */}
        <div className="bm-info-row">
          <span className="bm-label">Data e ora</span>
          <span className="bm-value">
            {new Date(booking.date).toLocaleDateString("it-IT")} — {booking.time}
          </span>
        </div>

        {/* Campo */}
        <div className="bm-info-row">
          <span className="bm-label">Campo</span>
          <span className="bm-value">
            {booking.facility.name} · {booking.facility.type}
          </span>
        </div>

        {/* Prenotato da */}
        <div className="bm-info-row">
          <span className="bm-label">Prenotato da</span>
          {booking.createdByHost ? (
            <span className="bm-value">Struttura</span>
          ) : (
            <Tooltip placement="topLeft" title={renderUserTooltip()}>
              <span className="bm-value bm-user-link">
                {booking.user.name} {booking.user.familyName}
              </span>
            </Tooltip>
          )}
        </div>

        {/* Stato */}
        <div className="bm-info-row">
          <span className="bm-label">Stato</span>
          <span className={`bm-status-badge ${statusClass}`}>
            {getStatusLabel(booking.status)}
          </span>
        </div>

        {/* Visibilità */}
        <div className="bm-info-row">
          <span className="bm-label">Visibilità</span>
          {mode === "view" ? (
            <span className="bm-value">
              {booking.scope === BookingScope.Open
                ? `Pubblica — Liv. min. ${booking.inviteLevel}`
                : "Privata"}
            </span>
          ) : isCustomTypeEdit ? (
            <span className="bm-value">Privata</span>
          ) : (
            <Select
              options={[
                { label: "Privata", value: BookingScope.Private },
                { label: "Pubblica", value: BookingScope.Open },
              ]}
              style={{ minWidth: 150 }}
              placeholder="Scegli visibilità"
              value={bookingData.scope}
              onSelect={handleFieldChange("scope")}
              getPopupContainer={(trigger) => trigger.parentNode}
            />
          )}
        </div>

        {/* Livello minimo (edit, solo se pubblica) */}
        {mode === "edit" && bookingData.scope === BookingScope.Open && (
          <div className="bm-field">
            <span className="bm-edit-label">Livello minimo partecipanti</span>
            <Input
              value={bookingData.inviteLevel}
              type="number"
              min={4.5}
              max={10.5}
              step={0.5}
              onChange={(e) => {
                let value = parseFloat(e.target.value);
                if (value < 4.5) value = 4.5;
                if (value > 10) value = 10;
                handleInviteLevelChange(value);
              }}
            />
          </div>
        )}

        {/* Colore del blocco nel calendario (solo prenotazioni della struttura) */}
        {mode === "view" && isValidHexColor(booking.color) && (
          <div className="bm-info-row">
            <span className="bm-label">Colore</span>
            <span className="bm-value">
              <span
                className="bk-color-dot"
                style={{ background: booking.color }}
              />
              {booking.color}
            </span>
          </div>
        )}
        {mode === "edit" && booking.createdByHost && (
          <div className="bm-field">
            <span className="bm-edit-label">Colore nel calendario</span>
            <BookingColorPicker
              value={bookingData.color}
              onChange={handleColorChange}
            />
          </div>
        )}

        {/* Condividi */}
        {canShare && (
          <div className="bm-info-row">
            <span className="bm-label">Condividi</span>
            <span className="bm-value bm-share">
              <a
                className="bm-share-btn bm-share-btn--whatsapp"
                href={getWhatsAppShareUrl(shareText)}
                target="_blank"
                rel="noopener noreferrer"
                title="Condividi la partita su WhatsApp"
              >
                <FontAwesomeIcon icon={faWhatsapp} />
                WhatsApp
              </a>
              <button
                type="button"
                className="bm-share-btn"
                onClick={handleCopyShareLink}
                title="Copia il messaggio con il link della partita"
              >
                <FontAwesomeIcon icon={faLink} />
                Copia link
              </button>
            </span>
          </div>
        )}
      </div>

      {/* ── Sezione: partecipanti ── */}
      {booking.match && (
        <div className="bm-section">
          <div className="bm-section-title">Partecipanti</div>

          {/* Formato singolare/doppio */}
          {[2, 7].includes(booking.sportId) && (
            <div className="bm-info-row">
              <span className="bm-label">Formato</span>
              {mode === "view" ? (
                <span className="bm-value">
                  {booking.match.matchType === MatchType.Single ? "Singolare" : "Doppio"}
                </span>
              ) : (
                <Select
                  options={[
                    { label: "Singolare", value: MatchType.Single },
                    { label: "Doppio", value: MatchType.Double },
                  ]}
                  style={{ minWidth: 150 }}
                  placeholder="Formato"
                  value={bookingData.scope}
                  onSelect={handleFieldChange("matchType")}
                  getPopupContainer={(trigger) => trigger.parentNode}
                />
              )}
            </div>
          )}

          {/* Team view */}
          {mode === "view" ? (
            <div className="bm-teams">
              <div className="bm-team">
                <span className="bm-team-label">Squadra 1</span>
                <div className="bm-team-player">{getPlayerName(booking.match.player1Team1)}</div>
                {isDouble && (
                  <div className="bm-team-player">{getPlayerName(booking.match.player2Team1)}</div>
                )}
              </div>
              <div className="bm-vs">VS</div>
              <div className="bm-team bm-team-right">
                <span className="bm-team-label">Squadra 2</span>
                <div className="bm-team-player">{getPlayerName(booking.match.player1Team2)}</div>
                {isDouble && (
                  <div className="bm-team-player">{getPlayerName(booking.match.player2Team2)}</div>
                )}
              </div>
            </div>
          ) : (
            <div className="d-flex flex-column gap-2 mt-1">
              <div className="bm-dnd-hint">
                <span>
                  Trascina la maniglia di un giocatore su un altro per scambiarli
                  {isDouble ? ", o l'etichetta di una coppia sull'altra" : ""}.
                </span>
                <button
                  type="button"
                  className="bm-swap-btn"
                  onClick={swapPairs}
                  disabled={!hasAnyPlayer}
                  title={isDouble ? "Inverti le coppie" : "Inverti i giocatori"}
                >
                  <FontAwesomeIcon icon={faRightLeft} className="me-1" />
                  {isDouble ? "Inverti coppie" : "Inverti"}
                </button>
              </div>

              {/* Coppia/Giocatore 1 */}
              <div
                className={pairClassName("team1")}
                style={{ borderTop: "none", paddingTop: 0 }}
                {...pairDropProps("team1")}
              >
                <span
                  className="bm-edit-label bm-pair-label"
                  title="Trascina sull'altra coppia per invertirle"
                  {...pairHandleProps("team1")}
                >
                  <FontAwesomeIcon icon={faGripVertical} className="me-1" />
                  {isDouble ? "Coppia" : "Giocatore"} 1
                </span>
                <div className="d-flex flex-column gap-2">
                  {!usersList ? <Spinner /> : (
                    <div
                      className={slotClassName("player1Team1")}
                      {...slotDropProps("player1Team1")}
                    >
                      {renderSlotHandle("player1Team1")}
                      <Select
                        showSearch
                        options={optionsFor("player1Team1")}
                        className="w-100 p-0"
                        filterOption={(input, option) =>
                          (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                        }
                        allowClear
                        placeholder="Giocatore 1"
                        value={bookingData.player1Team1}
                        onSelect={handleFieldChange("player1Team1")}
                        onClear={handleFieldClear("player1Team1")}
                        getPopupContainer={(trigger) => trigger.parentNode}
                      />
                    </div>
                  )}
                  {isDouble && (!usersList ? <Spinner /> : (
                    <div
                      className={slotClassName("player2Team1")}
                      {...slotDropProps("player2Team1")}
                    >
                      {renderSlotHandle("player2Team1")}
                      <Select
                        showSearch
                        options={optionsFor("player2Team1")}
                        className="w-100 p-0"
                        filterOption={(input, option) =>
                          (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                        }
                        placeholder="Giocatore 2"
                        value={bookingData.player2Team1}
                        onSelect={handleFieldChange("player2Team1")}
                        allowClear
                        onClear={handleFieldClear("player2Team1")}
                        getPopupContainer={(trigger) => trigger.parentNode}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Coppia/Giocatore 2 */}
              <div className={pairClassName("team2")} {...pairDropProps("team2")}>
                <span
                  className="bm-edit-label bm-pair-label"
                  title="Trascina sull'altra coppia per invertirle"
                  {...pairHandleProps("team2")}
                >
                  <FontAwesomeIcon icon={faGripVertical} className="me-1" />
                  {isDouble ? "Coppia" : "Giocatore"} 2
                </span>
                <div className="d-flex flex-column gap-2">
                  {!usersList ? <Spinner /> : (
                    <div
                      className={slotClassName("player1Team2")}
                      {...slotDropProps("player1Team2")}
                    >
                      {renderSlotHandle("player1Team2")}
                      <Select
                        showSearch
                        options={optionsFor("player1Team2")}
                        className="w-100 p-0"
                        filterOption={(input, option) =>
                          (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                        }
                        placeholder="Giocatore 1"
                        value={bookingData.player1Team2}
                        onSelect={handleFieldChange("player1Team2")}
                        allowClear
                        onClear={handleFieldClear("player1Team2")}
                        getPopupContainer={(trigger) => trigger.parentNode}
                      />
                    </div>
                  )}
                  {isDouble && (!usersList ? <Spinner /> : (
                    <div
                      className={slotClassName("player2Team2")}
                      {...slotDropProps("player2Team2")}
                    >
                      {renderSlotHandle("player2Team2")}
                      <Select
                        showSearch
                        options={optionsFor("player2Team2")}
                        className="w-100 p-0"
                        filterOption={(input, option) =>
                          (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                        }
                        placeholder="Giocatore 2"
                        value={bookingData.player2Team2}
                        onSelect={handleFieldChange("player2Team2")}
                        allowClear
                        onClear={handleFieldClear("player2Team2")}
                        getPopupContainer={(trigger) => trigger.parentNode}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Sezione: partecipanti CALCIO (lista giocatori con team/slot) ── */}
      {booking.sportId === 3 && (
        <FootballMatchParticipants
          bookingId={booking.id}
          usersList={usersList ?? []}
          editable={mode === "edit"}
        />
      )}

      {/* ── Sezione: partita di BASKET (formato, giocatori, presenze, punti) ── */}
      {booking.sportId === 4 && (
        <BasketMatchParticipants
          bookingId={booking.id}
          usersList={usersList ?? []}
          editable={mode === "edit"}
        />
      )}

      {/* ── Sezione: risultato ── */}
      {booking.match &&
        !booking.customType &&
        booking.bookingType !== BookingType.Training &&
        booking.status === BookingStatus.Completed && (
        <div className="bm-section">
          <div className="bm-section-title">Risultato</div>
          {booking.match.score.length === 0 ? (
            <span className="bm-empty">Nessun risultato inserito</span>
          ) : (
            <div className="bm-score-table">
              <div className="bm-score-row">
                <span className="bm-score-row-label"></span>
                {booking.match.score.map((_, i) => (
                  <span key={i} className="bm-score-header">Set {i + 1}</span>
                ))}
              </div>
              <div className="bm-score-row">
                <span className="bm-score-row-label">Sq. 1</span>
                {booking.match.score.map((s, i) => (
                  <div key={i} className="bm-score-cell">{s.team1}</div>
                ))}
              </div>
              <div className="bm-score-row">
                <span className="bm-score-row-label">Sq. 2</span>
                {booking.match.score.map((s, i) => (
                  <div key={i} className="bm-score-cell">{s.team2}</div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Sezione: note ── */}
      <div className="bm-section">
        <div className="bm-section-title">Note</div>
        {mode === "view" ? (
          <p className="bm-notes-text mb-0">{bookingData.notes || "—"}</p>
        ) : (
          <TextArea
            value={bookingData.notes}
            rows={3}
            onChange={handleNoteChange()}
            size="large"
            placeholder="Note..."
          />
        )}
      </div>

    </div>
  );
}
